import React, { useState } from "react";
import { Loader2, CheckCircle2, AlertCircle, Trash2, Lock } from "lucide-react";
import { adminApi } from "../../../lib/api";

const FIELDS = [
  { key: "customer_id", label: "Google Ads customer ID", hint: "Digits only — the number at the top right of Google Ads", type: "text" },
  { key: "client_id", label: "OAuth client ID", hint: "Ends in .apps.googleusercontent.com", type: "text" },
  { key: "client_secret", label: "OAuth client secret", hint: "From the same credential in Google Cloud", type: "password" },
  { key: "refresh_token", label: "Refresh token", hint: "The offline token generated for that client", type: "password" },
  { key: "developer_token", label: "Developer token (optional)", hint: "Only if Google asks for one — Tools → API Center", type: "password" },
  { key: "login_customer_id", label: "Manager account ID (optional)", hint: "Only if access comes through a manager account", type: "text" },
];

// Credentials go one way: in and encrypted. Nothing here can read them back, so
// the form shows what is connected rather than what was typed.
export default function AdsCredentialsForm({ status, onChange }) {
  const [form, setForm] = useState({ customer_id: status?.customer_id || "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await adminApi.googleAds.save(form);
      setForm({ customer_id: form.customer_id });
      onChange?.();
    } catch (err) {
      setError(err?.response?.data?.detail || "The connection could not be saved.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await adminApi.googleAds.remove();
      onChange?.();
    } catch {
      setError("The connection could not be removed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="bg-white rounded-xl border border-slate-200 p-6" data-testid="ads-credentials-form">
      <div className="flex items-start gap-3">
        <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
          <Lock size={16} />
        </span>
        <div>
          <h3 className="font-bold text-navy">Connect Google Ads</h3>
          <p className="text-sm text-slate-600 mt-1 leading-relaxed">
            Entered once and stored encrypted — they are never shown again and never reach the browser.
            The connection is read-only: it can run reports and nothing else.
          </p>
        </div>
      </div>

      {status?.source === "saved" && (
        <div className="mt-4 flex flex-wrap items-center gap-3 rounded-lg bg-emerald-50 px-4 py-3" data-testid="ads-connected-row">
          <CheckCircle2 size={16} className="text-emerald-600 shrink-0" />
          <p className="text-xs text-navy font-semibold">
            Account {status.customer_id}
            {status.saved_at ? ` · connected ${new Date(status.saved_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}` : ""}
          </p>
          <button
            type="button"
            onClick={remove}
            disabled={busy}
            data-testid="ads-disconnect"
            className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-red-500 duration-200 [transition-property:color] disabled:opacity-50"
          >
            <Trash2 size={12} /> Disconnect
          </button>
        </div>
      )}

      <div className="mt-5 grid sm:grid-cols-2 gap-4">
        {FIELDS.map((f) => (
          <label key={f.key} className="block">
            <span className="text-xs font-semibold text-navy">{f.label}</span>
            <input
              type={f.type}
              value={form[f.key] || ""}
              onChange={(e) => set(f.key, e.target.value)}
              autoComplete="off"
              spellCheck={false}
              data-testid={`ads-field-${f.key.replace(/_/g, "-")}`}
              className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-navy outline-none duration-200 [transition-property:border-color] focus:border-orange"
            />
            <span className="text-[11px] text-slate-400 mt-1 block">{f.hint}</span>
          </label>
        ))}
      </div>

      {error && (
        <p className="mt-4 flex items-start gap-2 text-xs text-red-600" data-testid="ads-credentials-error">
          <AlertCircle size={14} className="shrink-0 mt-px" /> <span>{error}</span>
        </p>
      )}

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button
          type="submit"
          disabled={busy}
          data-testid="ads-credentials-save"
          className="inline-flex items-center gap-2 rounded-full bg-navy px-5 py-2.5 text-xs font-bold text-white duration-200 [transition-property:background-color,opacity] hover:bg-navy/90 disabled:opacity-60"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : null}
          {busy ? "Checking with Google…" : status?.connected ? "Replace credentials" : "Connect and test"}
        </button>
        <span className="text-[11px] text-slate-400">
          Checked against the account before it is saved — nothing is kept if Google refuses.
        </span>
      </div>
    </form>
  );
}
